import { Product, ProductCategory } from '@api/types';
import {
  CategoryFilters,
  CategorySelector,
  CategorySelectorPlaceholder,
  NoResults,
  PageTitle,
  ProductList,
  ProductListPagination,
} from '@components';
import { useAppSelector } from '@hooks';
import { Box, styled } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';

// STYLES
//--------
const CategoryWrapper = styled(Box)`
  padding-bottom: 32px;
`;

const CategoryRow = styled(Box)`
  display: grid;
  gap: 16px;
  ${breakpoints.up('md')} {
    grid-template-columns: 280px auto;
    gap: 40px;
  }
  ${breakpoints.down('md')} {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

// TYPES
//-------

export type CategoryViewProps = {
  title?: string;
  skipEmptyCategories?: boolean;
  showFiltersAndPagination?: boolean;
  productItems?: Product[];
  totalCount?: number;
  categories?: ProductCategory[];
  isWorking?: boolean;
  handleCategoryChange: (category: ProductCategory) => void;
  isCategorySelected: (category: ProductCategory) => boolean;
  isCategoryOpened: (category: ProductCategory) => boolean;
};

// COMPONENTS
//------------

/**
 * Display products and categories provided from wrapper component
 */
export const CategoryView = ({
  title,
  showFiltersAndPagination,
  skipEmptyCategories,
  productItems,
  categories,
  isWorking,
  totalCount,
  isCategorySelected,
  handleCategoryChange,
  isCategoryOpened,
}: CategoryViewProps) => {
  const showNoResults = !isWorking && categories?.length === 0 && productItems?.length === 0;
  const isFiltersWorking = useAppSelector((state) => state.catalog.isFiltersWorking);
  const isCategoryInitialWorking = useAppSelector((state) => state.catalog.isInitialWorking);

  return (
    <CategoryWrapper>
      <PageTitle title={title} />
      {showNoResults && <NoResults />}
      {!showNoResults && (
        <CategoryRow>
          {isCategoryInitialWorking ? (
            <CategorySelectorPlaceholder />
          ) : (
            <CategorySelector
              skipEmptyCategories={skipEmptyCategories}
              totalCount={totalCount}
              categories={categories}
              isCategorySelected={isCategorySelected}
              showFiltersAndPagination={showFiltersAndPagination}
              handleCategoryChange={handleCategoryChange}
              isCategoryOpened={isCategoryOpened}
            />
          )}
          <div>
            {showFiltersAndPagination && !isFiltersWorking && (
              <CategoryFilters isWorking={isWorking} />
            )}
            <div className='product-list'>
              <ProductList productItems={productItems} isWorking={isWorking} />
              {showFiltersAndPagination && !isWorking && (
                <ProductListPagination totalCount={totalCount} />
              )}
            </div>
          </div>
        </CategoryRow>
      )}
    </CategoryWrapper>
  );
};
